// volume-tiers.ts — Volume discount tiers (book REGLES_DEVIS §4).
//
// Volume discount by cumulated-area tier. Thresholds 10/50/100/200/1000 m², rates 0/10/15/30/40/50 %.
// Cumulated PER CUMULATION CATEGORY: an in-house stock (UPZ) material and its off-stock equivalent
// share the same tiers. Applied retroactively and both ways (the cumulation is recomputed on every
// add/remove). Exclusions: tufted carpets and stands have their own rate tables.
//
// Ported from compute() of prototype v13.7.
//
// Deliberate divergence from prototype 13.7: the tier bound is STRICT (`<`), so the tier changes AT
// the exact threshold (10 m² exactly → 10 %), where the prototype applies `≤` (still 0 %).

import { type QuoteLine, num, unitM2, findMat, materialInFinishing } from "../core/core";
import { engineMessage } from "../core/messages";
import { isLargeFormatPrintRegime } from "../core/regimes";
import { ACTIVE_CATALOGUE, type Catalogue } from "../catalogue/catalogue-source";

/**
 * Volume discount tier + target margin, as provided by the Supabase table `margin_tiers` (seeded
 * from the MARGIN_TIERS_SEED table of catalogue 12). Ordered by ascending `sort_order`.
 */
export interface MarginTier {
  /** Index 0..5, one per tier (also the order of the target margins). */
  sort_order: number;
  /** Lower threshold, INCLUSIVE (m² or linear m). Selection uses this threshold (exact → upper tier). */
  minThreshold: number;
  /** Informative upper threshold (null = last tier, unbounded). */
  maxThreshold: number | null;
  /** Volume discount rate of the tier (0..1). */
  discountRate: number;
  /** Target margin of the tier (0..1) — margin = (PV − PA) / PV. */
  targetMargin: number;
}

/**
 * Rate table injected into compute(): tiers and target margins are NO LONGER embedded in the engine
 * (the only exception to the embedded-reference principle). The application reads it from Supabase and
 * passes it in; a read failure is an explicit upstream error, never a silent default.
 */
export interface VolumeGrid {
  tiers: readonly MarginTier[];
}

/**
 * Guard: a missing or empty rate table is an explicit error (never a silent default).
 * @param grid The rate table to check.
 * @throws Error when the table is missing or empty.
 */
export function assertVolumeGrid(grid: VolumeGrid | undefined | null): asserts grid is VolumeGrid {
  if (!grid || !Array.isArray(grid.tiers) || grid.tiers.length === 0) {
    throw new Error(engineMessage("engineVolumeGridMissing"));
  }
}

/**
 * Tier applicable to a cumulation (m² for print, linear m for a standalone accessory). Bound on
 * the minimum threshold: the highest tier whose `minThreshold ≤ cumulation` wins. The change happens
 * AT the exact threshold (10 m² exactly → 10 %), per the MARGIN_TIERS_SEED table of catalogue 12.
 * @param cumul The cumulation (m² or linear m).
 * @param grid The rate table.
 * @returns The applicable tier.
 */
export function marginTier(cumul: number, grid: VolumeGrid): MarginTier {
  let hit = grid.tiers[0]!;
  for (const p of grid.tiers) if (cumul >= p.minThreshold) hit = p;
  return hit;
}

/**
 * Target margin (0..1) applicable to a cumulation, per the rate table.
 * @param cumul The cumulation (m² or linear m).
 * @param grid The rate table.
 * @returns The target margin of the applicable tier.
 */
export function targetMarginForTier(cumul: number, grid: VolumeGrid): number {
  return marginTier(cumul, grid).targetMargin;
}

/**
 * Tier label for a cumulation, per the rate table: "10-50" = [10, 50), ">1000" for the last one.
 * @param cumul The cumulation (m² or linear m).
 * @param grid The rate table.
 * @returns The tier label.
 */
export function m2Label(cumul: number, grid: VolumeGrid): string {
  const p = marginTier(cumul, grid);
  return p.maxThreshold == null ? ">" + p.minThreshold : p.minThreshold + "-" + Math.ceil(p.maxThreshold);
}

/**
 * Cumulation key of a print line: material category (`Material.category`), suffixed with the laminated
 * material's category where applicable (distinct flat-fee group). Returns null for any line that does
 * not count towards volume tiers (non-print, no material, material not found).
 * @param l The quote line.
 * @param cat The catalogue.
 * @returns The cumulation key, or null.
 */
export function volumeCategory(l: QuoteLine, cat: Catalogue = ACTIVE_CATALOGUE): string | null {
  if (!isLargeFormatPrintRegime(l) || !l.material) return null;
  const materialEntry = findMat(l.material, cat);
  if (!materialEntry) return null;
  const cRef = materialInFinishing(l, cat);
  const matC = cRef ? findMat(cRef, cat) : undefined;
  return (materialEntry.category || l.material) + (matC ? " + " + (matC.category || cRef) : "");
}

/**
 * Cumulated area per category across all quote lines (EXACT area, not rounded — the total is computed
 * on this basis, the 3-decimal rounding is display only).
 * @param lines The quote lines.
 * @param catalogue The catalogue.
 * @returns The cumulated area (m²) per category.
 */
export function cumulateByCategory(lines: readonly QuoteLine[], catalogue: Catalogue = ACTIVE_CATALOGUE): Map<string, number> {
  const cumul = new Map<string, number>();
  for (const l of lines) {
    const cat = volumeCategory(l, catalogue);
    if (cat == null) continue;
    const surf = unitM2(l) * num(l.fileQty) * num(l.copiesPerFile);
    cumul.set(cat, (cumul.get(cat) ?? 0) + surf);
  }
  return cumul;
}

/**
 * Volume tier applicable to each category, from its cumulated area.
 * @param lines The quote lines.
 * @param grid The rate table.
 * @param catalogue The catalogue.
 * @returns The applicable tier per category.
 */
export function tierByCategory(
  lines: readonly QuoteLine[],
  grid: VolumeGrid,
  catalogue: Catalogue = ACTIVE_CATALOGUE,
): Map<string, MarginTier> {
  const out = new Map<string, MarginTier>();
  for (const [cat, m2] of cumulateByCategory(lines, catalogue)) out.set(cat, marginTier(m2, grid));
  return out;
}
